import React from 'react';
import type {SetRequired} from 'type-fest';
import {type FieldValues, type FieldPath} from 'react-hook-form';
import {splitEventHandler} from '@netx/core-next/utils';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {combineRefs} from '@netx/core-components/util';
import {TextField, type TextFieldProps} from '@netx/core-components/TextField';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormTextFieldProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		SetRequired<TextFieldProps, 'name'>,
		TFieldValues,
		TName
	> {}

export const ReactHookFormTextFieldPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		ControllerProps,
		...props
	}: ReactHookFormTextFieldProps<TFieldValues, TName>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => {
				return (
					<TextField
						error={formState.errors[props.name]?.message?.toString()}
						{...props}
						{...field}
						disabled={props.disabled || field.disabled}
						ref={ref}
						inputRef={combineRefs(props.inputRef, field.ref)}
						value={field.value ?? ''}
						onChange={useConstCallback(
							splitEventHandler(props.onChange, (event, value) =>
								field.onChange(value),
							),
						)}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `TextField` component
 */
export const ReactHookFormTextField = React.forwardRef(
	ReactHookFormTextFieldPrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormTextFieldProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormTextField;
